
import React, { useState, useMemo } from 'react';
import { Button } from './Button';
import { BuybackDeviceType, BuybackCondition, DeviceModelInfo, BuybackPricingFactors } from '../types';
import { BUYBACK_DEVICE_TYPES, BUYBACK_CONDITIONS } from '../constants';
import type { QuickAppraisalInitialData } from '../App';

interface QuickAppraisalWidgetProps {
  onNavigateToFullBuyback: (initialData?: QuickAppraisalInitialData) => void;
  buybackDeviceModels: Record<BuybackDeviceType, DeviceModelInfo[]>;
  buybackPricingFactors: BuybackPricingFactors;
}

const CalculatorIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-[#003794]" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18m2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18m2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18m2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6.75h7.5v.75h-7.5V6.75zM12 4.5v3m0 0v3m0-3h3m-3 0h-3" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 6.75A2.25 2.25 0 015.25 4.5h13.5A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75z" />
  </svg>
);


export const QuickAppraisalWidget: React.FC<QuickAppraisalWidgetProps> = ({
    onNavigateToFullBuyback,
    buybackDeviceModels,
    buybackPricingFactors
}) => {
  const [deviceType, setDeviceType] = useState<BuybackDeviceType | ''>('');
  const [modelId, setModelId] = useState<string>('');
  const [condition, setCondition] = useState<BuybackCondition | ''>('');
  const [storage, setStorage] = useState<string>('');

  const availableModels = useMemo(() => {
    return deviceType ? (buybackDeviceModels[deviceType] || []) : [];
  }, [deviceType, buybackDeviceModels]);

  const selectedModelInfo = useMemo(() => {
    return availableModels.find(m => m.id === modelId);
  }, [availableModels, modelId]);

  const estimatedValue = useMemo(() => {
    if (!selectedModelInfo || !condition) return 0;

    let value = selectedModelInfo.baseValue;
    value *= buybackPricingFactors.conditionMultiplier[condition];

    if (storage && selectedModelInfo.storageOptions) {
        const storageFactorKey = storage as keyof typeof buybackPricingFactors.storageBonusPercentage;
        const storageFactor = buybackPricingFactors.storageBonusPercentage[storageFactorKey] || 0;
        value += selectedModelInfo.baseValue * storageFactor;
    }

    if (condition === BuybackCondition.POOR_BROKEN) {
        value = Math.max(value, buybackPricingFactors.MINIMUM_POOR_VALUE);
    }
    return Math.max(0, parseFloat(value.toFixed(2)));
  }, [selectedModelInfo, condition, storage, buybackPricingFactors]);

  const handleDeviceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDeviceType(e.target.value as BuybackDeviceType | '');
    setModelId('');
    setCondition('');
    setStorage('');
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModelId(e.target.value);
    setCondition('');
    setStorage('');
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCondition(e.target.value as BuybackCondition | '');
  };

  const handleStorageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStorage(e.target.value);
  };

  const handleGetFullQuote = () => {
    const initialData: QuickAppraisalInitialData = {};
    if (deviceType) initialData.deviceType = deviceType;
    if (modelId) initialData.modelId = modelId;
    if (condition) initialData.condition = condition;
    onNavigateToFullBuyback(initialData);
  };

  const commonSelectClass = "w-full mt-1 px-3 py-2 border border-slate-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#003794] focus:border-[#003794] sm:text-sm";

  return (
    <div>
      <div className="flex items-center mb-4">
        <CalculatorIcon />
        <h2 className="text-xl font-semibold text-slate-700">Quick Device Appraisal (Apple Only)</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 items-end">
        <div>
          <label htmlFor="quickDeviceType" className="block text-sm font-medium text-slate-700">Device Type</label>
          <select id="quickDeviceType" value={deviceType} onChange={handleDeviceTypeChange} className={commonSelectClass}>
            <option value="">-- Select Type --</option>
            {BUYBACK_DEVICE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="quickModelId" className="block text-sm font-medium text-slate-700">Model</label>
          <select id="quickModelId" value={modelId} onChange={handleModelChange} className={commonSelectClass} disabled={!deviceType || availableModels.length === 0}>
            <option value="">-- Select Model --</option>
            {availableModels.map(model => <option key={model.id} value={model.id}>{model.name}</option>)}
          </select>
        </div>
        {selectedModelInfo?.storageOptions && (
            <div>
                <label htmlFor="quickStorage" className="block text-sm font-medium text-slate-700">Storage/Specifics</label>
                <select id="quickStorage" value={storage} onChange={handleStorageChange} className={commonSelectClass} disabled={!modelId}>
                    <option value="">-- Select Storage --</option>
                    {selectedModelInfo.storageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
        )}
        <div>
          <label htmlFor="quickCondition" className="block text-sm font-medium text-slate-700">Condition</label>
          <select id="quickCondition" value={condition} onChange={handleConditionChange} className={commonSelectClass} disabled={!modelId}>
            <option value="">-- Select Condition --</option>
            {BUYBACK_CONDITIONS.map(cond => <option key={cond} value={cond}>{cond}</option>)}
          </select>
        </div>
      </div>

      {condition && selectedModelInfo && (
        <div className="p-4 bg-slate-100 rounded-lg text-center shadow-inner mb-4">
            <p className="text-md text-slate-600 mb-1">Estimated Buyback Value:</p>
            <p className="text-3xl font-bold text-[#003794]">${estimatedValue.toFixed(2)}</p>
            <p className="text-xs text-slate-500 mt-1">This is a preliminary estimate.</p>
        </div>
      )}

      <div className="text-right">
        <Button
            onClick={handleGetFullQuote}
            variant="secondary"
            disabled={!deviceType || !modelId || !condition}
            className="bg-[#FFa800] text-black hover:bg-[#e69500]"
        >
            Get Full Quote / Start Buyback
        </Button>
      </div>
       <p className="text-xs text-slate-500 mt-3">
        The Quick Appraisal provides an estimate based on model and general condition. For a more accurate quote including accessories and detailed condition, please proceed to the full buyback form.
      </p>
    </div>
  );
};
